import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  subscribeCustody,
  tryAgainUnsavedCaptures,
  unsavedRecordingState,
} from '@/lib/voice-capture-custody';
import { useNoticeSlot } from '@/lib/app-notice-stack';
import styles from './UnsavedRecordingWarning.module.css';

/**
 * App-level warning while this page holds a recording that is not saved yet
 * (account-change recording plan rev 4.1, section 8): a `failed` capture (its
 * bytes kept only in this page's memory) or an `unresolved` one (its stop
 * event is overdue). Mounted beside the auth gate, so it also shows on the
 * sign-in screen. It names no account and shows no content. Try again commits
 * the kept capture to its own owner (never processed, so safe under any
 * session). While the warning applies, closing or reloading asks for
 * confirmation where the browser honours it (iOS Safari may not).
 */
export default function UnsavedRecordingWarning() {
  const { t } = useTranslation();
  const [state, setState] = useState(unsavedRecordingState);
  const [busy, setBusy] = useState(false);
  const [stillFailed, setStillFailed] = useState(false);
  // Presentation only: rendered into the app notice stack's warning slot.
  const slot = useNoticeSlot('warning');

  useEffect(() => subscribeCustody(() => {
    const next = unsavedRecordingState();
    setState((current) => (
      current.failed === next.failed && current.unresolved === next.unresolved ? current : next
    ));
  }), []);

  const unsaved = state.failed > 0 || state.unresolved > 0;

  useEffect(() => {
    if (!unsaved) {
      setStillFailed(false);
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Some browsers still require a return value to show their own prompt.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [unsaved]);

  if (!unsaved || !slot) return null;

  const tryAgain = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const saved = await tryAgainUnsavedCaptures();
      setStillFailed(!saved);
    } catch {
      setStillFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const message = state.failed > 0
    ? stillFailed
      ? t('unsavedRecording.stillFailed')
      : t('unsavedRecording.body')
    : t('unsavedRecording.finishing');

  return createPortal(
    <div className={styles.warning} role="alert" aria-live="assertive">
      <div className={styles.text}>
        <p className={styles.title}>{t('unsavedRecording.title')}</p>
        <p className={styles.message}>{message}</p>
      </div>
      {state.failed > 0 && (
        <button
          type="button"
          className={styles.retry}
          disabled={busy}
          onClick={() => { void tryAgain(); }}
        >
          {t('unsavedRecording.tryAgain')}
        </button>
      )}
    </div>,
    slot,
  );
}
